import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchSchedules, createSchedule, updateSchedule, deleteSchedule,
  type ScheduleView, type ScheduleInput,
} from '../../lib/api/schedules';
import { validateSchedule, TASK_TYPES } from '../../../../shared/lib/schedule';

/**
 * FORGE-179 Phase 4 — Schedules tab. Business/Enterprise clients create recurring
 * security-review / performance / custom runs (cron + prompt + repos) that open a
 * PR with high-confidence fixes. Mirrors the orchestrator's `validateSchedule`
 * so a schedule that saves is one the scanner fires. Plan-gated server-side; the
 * UI reflects the `gated` flag from the list response.
 */

const CRON_PRESETS: [string, string][] = [
  ['0 2 * * *', 'Daily · 02:00 UTC'],
  ['0 2 * * 1', 'Weekly · Mon 02:00 UTC'],
  ['0 2 1 * *', 'Monthly · 1st 02:00 UTC'],
  ['0 */6 * * *', 'Every 6 hours'],
  ['', 'Custom…'],
];

const TASK_LABELS: Record<string, string> = {
  'security-review': 'Security review',
  'performance': 'Performance',
  'custom': 'Custom',
};

interface DraftState extends ScheduleInput { id?: string }
const blank = (): DraftState => ({ name: '', task_type: 'security-review', prompt: '', repos: [], cron: '0 2 * * *', enabled: true });

export const SchedulesTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['project', projectId, 'schedules'],
    queryFn: () => fetchSchedules(projectId),
    enabled: projectId.length > 0,
  });

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [customCron, setCustomCron] = useState(false);

  const errors = useMemo(() => (draft ? validateSchedule(draft) : []), [draft]);

  if (q.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (q.isError) return <div role="alert" className="text-sm text-red-600">{(q.error as Error).message}</div>;
  const data = q.data!;
  const refresh = () => qc.invalidateQueries({ queryKey: ['project', projectId, 'schedules'] });

  const reposText = (r: string[]) => r.join('\n');
  const parseRepos = (t: string) => t.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

  const save = async () => {
    if (!draft) return;
    setError(null);
    if (errors.length) { setError(errors[0]); return; }
    setBusy(true);
    try {
      const body: ScheduleInput = {
        name: draft.name, task_type: draft.task_type, prompt: draft.prompt,
        repos: draft.repos, cron: draft.cron, enabled: draft.enabled,
        ...(draft.provider ? { provider: draft.provider, model: draft.model } : {}),
      };
      if (draft.id) await updateSchedule(projectId, draft.id, body);
      else await createSchedule(projectId, body);
      setDraft(null);
      refresh();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const remove = async (s: ScheduleView) => {
    setBusy(true);
    try { await deleteSchedule(projectId, s.id); refresh(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const openEdit = (s: ScheduleView) => {
    setCustomCron(!CRON_PRESETS.some(([c]) => c === s.cron && c !== ''));
    setDraft({ id: s.id, name: s.name, task_type: s.task_type, prompt: s.prompt, repos: s.repos, cron: s.cron, enabled: s.enabled, provider: s.provider ?? undefined, model: s.model ?? undefined });
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="schedules-tab">
      <p className="text-sm text-slate-600">
        Recurring maintenance runs. Each fires on its schedule and opens a PR with high-confidence
        fixes (uncertain findings are listed in the PR for review — nothing is auto-merged).
      </p>

      {!data.gated && (
        <div className="text-sm rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2" data-testid="schedules-plan-lock">
          Scheduled tasks require the <strong>Business</strong> or <strong>Enterprise</strong> plan.
          You can view and delete existing schedules, but creating new ones is disabled.
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {data.schedules.length === 0 && <div className="text-sm text-slate-500">No schedules yet.</div>}
        {data.schedules.map((s) => (
          <div key={s.id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`schedule-${s.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">
                {s.name} <span className="text-xs text-slate-400">· {TASK_LABELS[s.task_type] ?? s.task_type}</span>
                {!s.enabled && <span className="ml-2 text-xs text-slate-400">(disabled)</span>}
              </div>
              <div className="text-xs text-slate-500 font-mono">{s.cron} · {s.repos.length} repo(s)</div>
              {s.last_run_at && <div className="text-xs text-slate-400">last: {s.last_run_status ?? '—'} {new Date(s.last_run_at).toLocaleString()}</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" className="text-xs text-indigo-600" onClick={() => openEdit(s)} data-testid={`schedule-edit-${s.id}`}>Edit</button>
              <button type="button" className="text-xs text-red-600" onClick={() => remove(s)} disabled={busy}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {!draft && data.gated && (
        <button type="button" data-testid="schedule-add" onClick={() => { setDraft(blank()); setCustomCron(false); }}
          className="text-sm font-medium px-3 py-2 rounded-md border border-indigo-300 text-indigo-700">
          + New schedule
        </button>
      )}

      {/* Editor */}
      {draft && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="schedule-editor">
          <input className="w-full text-sm border border-slate-300 rounded-md px-2 py-1" placeholder="Name (e.g. Nightly security)"
            data-testid="schedule-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />

          <div className="flex gap-3">
            <select className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white" data-testid="schedule-tasktype"
              value={draft.task_type} onChange={(e) => setDraft({ ...draft, task_type: e.target.value })}>
              {TASK_TYPES.map((t) => <option key={t} value={t}>{TASK_LABELS[t]}</option>)}
            </select>
            <label className="text-sm flex items-center gap-1">
              <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> Enabled
            </label>
          </div>

          <div className="flex gap-2 items-center">
            <select className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white" data-testid="schedule-cron-preset"
              value={customCron ? '' : draft.cron}
              onChange={(e) => { const v = e.target.value; if (v === '') { setCustomCron(true); } else { setCustomCron(false); setDraft({ ...draft, cron: v }); } }}>
              {CRON_PRESETS.map(([c, lbl]) => <option key={lbl} value={c}>{lbl}</option>)}
            </select>
            {customCron && (
              <input className="text-sm font-mono border border-slate-300 rounded-md px-2 py-1" placeholder="0 2 * * 1"
                data-testid="schedule-cron-custom" value={draft.cron} onChange={(e) => setDraft({ ...draft, cron: e.target.value })} />
            )}
            <span className="text-xs text-slate-400">UTC</span>
          </div>

          <textarea className="w-full text-sm border border-slate-300 rounded-md px-2 py-1 font-mono" rows={2}
            placeholder="Target repos — one org/repo per line" data-testid="schedule-repos"
            value={reposText(draft.repos)} onChange={(e) => setDraft({ ...draft, repos: parseRepos(e.target.value) })} />

          <textarea className="w-full text-sm border border-slate-300 rounded-md px-2 py-1" rows={4}
            placeholder="Prompt — what this run should look for and fix" data-testid="schedule-prompt"
            value={draft.prompt} onChange={(e) => setDraft({ ...draft, prompt: e.target.value })} />

          {/* Optional per-schedule model. Default = the project's bugfix-agent model. */}
          <div className="flex gap-2 items-center">
            <label className="text-xs text-slate-500 w-12">Model</label>
            <select className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white" data-testid="schedule-provider"
              value={draft.provider ?? ''}
              onChange={(e) => { const p = e.target.value; setDraft({ ...draft, provider: p || undefined, model: p ? draft.model : undefined }); }}>
              <option value="">Project default</option>
              {(data.allowedProviders ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {draft.provider && (
              <input className="text-sm font-mono border border-slate-300 rounded-md px-2 py-1 flex-1" data-testid="schedule-model"
                placeholder="model id (e.g. claude-sonnet-4-6)" value={draft.model ?? ''}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })} />
            )}
          </div>

          {errors.length > 0 && <ul className="text-sm text-red-600 list-disc list-inside" data-testid="schedule-errors">{errors.map((er, i) => <li key={i}>{er}</li>)}</ul>}
          {error && <div role="alert" className="text-sm text-red-600">{error}</div>}

          <div className="flex gap-2">
            <button type="button" data-testid="schedule-save" disabled={busy || errors.length > 0} onClick={save}
              className="text-sm font-medium px-3 py-1.5 rounded-md bg-indigo-600 text-white disabled:opacity-40">
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="text-sm px-3 py-1.5 text-slate-600" onClick={() => { setDraft(null); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
